import { Play } from "lucide-react";
import { motion } from "framer-motion";

const WhyUs = () => {
  return (
    <section className="py-24 bg-white border-t border-neutral-200 overflow-hidden" id="galerie">
      <div className="container mx-auto px-4 md:px-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16 max-w-3xl"
        >
          <span className="font-semibold text-primary uppercase tracking-wider text-sm">Überzeugende Qualität</span>
          <h2 className="font-heading text-3xl md:text-5xl mt-2 mb-6">Warum unsere Kunden uns lieben</h2>
          <p className="text-lg text-neutral-600 font-medium">
            Wir behandeln Ihr Hab und Gut, als wäre es unser eigenes. Sehen Sie selbst, wie unser Team mit höchster Sorgfalt, Effizienz und Professionalität arbeitet.
          </p>
        </motion.div>

        {/* Premium Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6">
          
          {/* 16:9 Video Style - Span 3 Cols */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative group overflow-hidden md:col-span-3 bg-black rounded-xl border border-neutral-200 aspect-video [transform:translateZ(0)]"
          >
            <video 
              muted
              loop
              playsInline
              preload="none"
              poster="https://images.leadconnectorhq.com/image/f_webp/q_80/r_800/u_https://assets.cdn.filesafe.space/Zrqvy6cNzSbnzDV55cv1/media/6936df931da73c728e8789a3.jpg"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" 
              onMouseOver={(e) => e.currentTarget.play().catch(() => {})}
              onTouchStart={(e) => e.currentTarget.play().catch(() => {})}
            >
              <source src="https://assets.cdn.filesafe.space/Zrqvy6cNzSbnzDV55cv1/media/6936df93b480b32736874411.mp4" type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-80 pointer-events-none"></div>
            <div className="absolute bottom-6 left-6 right-6 pointer-events-none">
              <p className="text-white font-bold text-2xl leading-tight drop-shadow-md">Schnell & Effizient</p>
            </div>
          </motion.div>

          {/* Right Column (Stat Card + Image 1) */}
          <div className="md:col-span-1 flex flex-col gap-4 md:gap-6">
            {/* Stat / Trust Card */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex-1 relative overflow-hidden bg-neutral-900 text-white p-6 flex flex-col justify-center border-l-4 border-primary rounded-xl min-h-[160px]"
            >
              <h3 className="font-heading text-3xl xl:text-4xl text-primary mb-2">+10 Jahre</h3>
              <p className="text-base xl:text-lg font-bold mb-1">Erfahrung</p>
              <p className="text-xs xl:text-sm text-neutral-400 font-medium">In der Umzugsbranche</p>
              
              <div className="mt-4 pt-4 border-t border-white/10">
                 <p className="text-xs xl:text-sm text-neutral-300 flex items-center gap-2 font-medium">
                   <span className="w-2 h-2 rounded-full bg-green-500 inline-block shrink-0"></span>
                   Voll versichert
                 </p>
              </div>
            </motion.div>

            {/* Square Image 1 */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex-1 relative group overflow-hidden bg-neutral-100 rounded-xl min-h-[160px] [transform:translateZ(0)]"
            >
              <img 
                src="https://images.leadconnectorhq.com/image/f_webp/q_80/r_800/u_https://assets.cdn.filesafe.space/Zrqvy6cNzSbnzDV55cv1/media/6936df931da73c728e8789a3.jpg" 
                alt="Möbel werden sicher verpackt" 
                width="800"
                height="800"
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-80 pointer-events-none"></div>
              <div className="absolute bottom-4 left-4 right-4 pointer-events-none">
                <p className="text-white font-bold text-sm xl:text-base leading-tight drop-shadow-md">Sicher verpackt</p>
              </div>
            </motion.div>
          </div>

          {/* Bottom Row: 3 Images */}
          {/* Square Image 2 */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="md:col-span-1 relative group overflow-hidden bg-neutral-100 rounded-xl aspect-square md:aspect-auto md:h-64 [transform:translateZ(0)]"
          >
            <img 
              src="https://images.leadconnectorhq.com/image/f_webp/q_80/r_800/u_https://assets.cdn.filesafe.space/Zrqvy6cNzSbnzDV55cv1/media/6936df934b202f5e27b43c42.jpg" 
              alt="Professionelles Team" 
              width="800"
              height="800"
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors duration-500 pointer-events-none"></div>
          </motion.div>

          {/* Wide Image 3 */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="md:col-span-2 relative group overflow-hidden bg-neutral-100 rounded-xl aspect-video md:aspect-auto md:h-64 [transform:translateZ(0)]"
          >
            <img 
              src="https://images.leadconnectorhq.com/image/f_webp/q_80/r_800/u_https://assets.cdn.filesafe.space/Zrqvy6cNzSbnzDV55cv1/media/6936df93efa09c211e149b1d.jpg" 
              alt="Umzugswagen" 
              width="1200"
              height="675"
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors duration-500 pointer-events-none"></div>
          </motion.div>

          {/* Square Image 4 */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="md:col-span-1 relative group overflow-hidden bg-neutral-100 rounded-xl aspect-square md:aspect-auto md:h-64 [transform:translateZ(0)]"
          >
            <img 
              src="https://images.leadconnectorhq.com/image/f_webp/q_80/r_800/u_https://assets.cdn.filesafe.space/Zrqvy6cNzSbnzDV55cv1/media/6936df9351010807725c6b00.jpg" 
              alt="Sorgfältige Arbeit" 
              width="800"
              height="800"
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors duration-500 pointer-events-none"></div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default WhyUs;